import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy/button';
import { Modal } from '@/components/ui/daisy/modal';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import { Colors } from '@/components/ui/types';

import { ConfirmModalProps, ConfirmModalVariant } from './types';

const ConfirmModal = ({
  visible,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  isLoading = false,
  variant = 'destructive',
}: ConfirmModalProps) => {
  const { ref: dialogRef, close } = useModalDialog(visible, onClose);

  useEffect(() => {
    if (!visible && dialogRef.current?.open) close();
  }, [visible, close, dialogRef]);

  return (
    <Modal ref={dialogRef}>
      <ConditionalRenderer condition={visible}>
        <Modal.Box>
          <ConfirmModalContent
            onClose={close}
            onConfirm={onConfirm}
            title={title}
            description={description}
            confirmLabel={confirmLabel}
            cancelLabel={cancelLabel}
            isLoading={isLoading}
            variant={variant}
          />
          <Modal.Close disabled={isLoading} />
        </Modal.Box>
        <Modal.Backdrop>
          <button
            type="button"
            onClick={isLoading ? undefined : close}
            disabled={isLoading}
          >
            close
          </button>
        </Modal.Backdrop>
      </ConditionalRenderer>
    </Modal>
  );
};

type ConfirmModalContentProps = Omit<ConfirmModalProps, 'visible'>;

const ConfirmModalContent = ({
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  isLoading,
  variant = 'destructive',
}: ConfirmModalContentProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'confirmModal',
  });

  const mapVariantToColor: Record<ConfirmModalVariant, Colors> = {
    destructive: 'error',
    default: 'primary',
  };

  const mapVariantToConfirmLabel: Record<ConfirmModalVariant, string> = {
    destructive: t('confirmDestructive'),
    default: t('confirmDefault'),
  };

  return (
    <>
      <h3 className="text-lg font-bold pr-8">{title ?? t('defaultTitle')}</h3>
      <ConditionalRenderer condition={!!description}>
        <div className="py-4 text-base-content/80">{description}</div>
      </ConditionalRenderer>
      <Modal.Action className="flex">
        <Button
          className="grow"
          variant="soft"
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelLabel ?? t('cancel')}
        </Button>
        <Button
          className="grow"
          color={mapVariantToColor[variant]}
          onClick={onConfirm}
          loading={isLoading}
        >
          {confirmLabel ?? mapVariantToConfirmLabel[variant]}
        </Button>
      </Modal.Action>
    </>
  );
};

export default ConfirmModal;
